import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { casesAPI, activitiesAPI, documentsAPI, notificationsAPI, usersAPI } from '../services/api';
import api from '../services/api';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import {
  ArrowLeft, Plus, Upload, Bell, FileText, MessageSquare,
  Sparkles, X, RefreshCw, Edit2, Gavel, ScrollText, ClipboardList,
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';

// ── Status color map (case-insensitive via toLowerCase lookup) ─────────────────
const STATUS_COLORS = {
  active: 'bg-green-100 text-green-700',
  adjourned: 'bg-yellow-100 text-yellow-700',
  disposed: 'bg-gray-100 text-gray-600',
  stayed: 'bg-purple-100 text-purple-700',
  pending: 'bg-blue-100 text-blue-700',
  closed: 'bg-red-100 text-red-700',
  'under trial': 'bg-orange-100 text-orange-700',
  'decree passed': 'bg-teal-100 text-teal-700',
  'appeal filed': 'bg-indigo-100 text-indigo-700',
  execution: 'bg-pink-100 text-pink-700',
  settled: 'bg-emerald-100 text-emerald-700',
  withdrawn: 'bg-slate-100 text-slate-600',
};

const statusColor = (val) =>
  STATUS_COLORS[(val || '').toLowerCase()] || 'bg-gray-100 text-gray-600';

// ── Tab definitions ────────────────────────────────────────────────────────────
const TABS = [
  { key: 'hearing_history', label: 'Hearing History', Icon: Gavel          },
  { key: 'interim_orders', label: 'Interim Orders',  Icon: ScrollText     },
  { key: 'processes',      label: 'Processes',        Icon: ClipboardList  },
  { key: 'documents',      label: 'Documents',        Icon: FileText       },
  { key: 'activities',     label: 'Activities',      Icon: MessageSquare  },
];

// ── Date formatter helper ──────────────────────────────────────────────────────
const fmtDate = (val) => {
  if (!val) return '—';
  try { return format(new Date(val), 'dd MMM yyyy'); }
  catch { return val; }
};

const fmtDateTime = (val) => {
  if (!val) return 'Never synced';
  try { return format(new Date(val), 'dd MMM yyyy HH:mm'); }
  catch { return val; }
};

// ── Priority colour ────────────────────────────────────────────────────────────
const priorityColor = (p) =>
  p === 'high' ? 'text-red-600' : p === 'medium' ? 'text-yellow-600' : 'text-green-600';

// ═══════════════════════════════════════════════════════════════════════════════
export default function CaseDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  // ── Core state ───────────────────────────────────────────────────────────────
  const [caseData, setCaseData]       = useState(null);
  const [activities, setActivities]   = useState([]);
  const [documents, setDocuments]     = useState([]);
  const [tab, setTab]                 = useState('hearing_history');
  const [loading, setLoading]         = useState(true);
  const [caseStages, setCaseStages]   = useState([]);

  // ── Sync ─────────────────────────────────────────────────────────────────────
  const [syncing, setSyncing] = useState(false);

  // ── Edit mode ────────────────────────────────────────────────────────────────
  const [editing, setEditing]         = useState(false);
  const [saving, setSaving]           = useState(false);
  const [lawyers, setLawyers]         = useState([]);
  const [editForm, setEditForm]       = useState({
    priority: 'medium',
    assigned_lawyer: null,
    supporting_lawyers: [],
    internal_notes: '',
  });

  // ── Activity form ────────────────────────────────────────────────────────────
  const [showActivityForm, setShowActivityForm] = useState(false);
  const [actForm, setActForm] = useState({
    activity_type: 'next_hearing',
    description: '',
    new_hearing_date: '',
    new_status: '',
  });

  // ── Document upload ──────────────────────────────────────────────────────────
  const [uploading, setUploading] = useState(false);

  // ── Notification ─────────────────────────────────────────────────────────────
  const [sending, setSending] = useState(false);

  // ── AI Summary ───────────────────────────────────────────────────────────────
  const [summary, setSummary]             = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [showSummary, setShowSummary]     = useState(false);

  // ── Shared input class ───────────────────────────────────────────────────────
  const inputCls = 'w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500';

  // ── Mount guard to prevent state-after-unmount and stale-load races ─────────
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  // ── Data loading ─────────────────────────────────────────────────────────────
  const load = async () => {
    setLoading(true);
    try {
      const [c, a, d] = await Promise.all([
        casesAPI.detail(id),
        activitiesAPI.list(id),
        documentsAPI.list(id),
      ]);
      if (!mountedRef.current) return;
      setCaseData(c.data);
      setActivities(a.data.results || a.data);
      setDocuments(d.data.results || d.data);
    } catch {
      if (mountedRef.current) toast.error('Failed to load case');
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  };

  useEffect(() => {
    load();
    api.get('/masters/case-stages/')
      .then(r => { if (mountedRef.current) setCaseStages(r.data.results || r.data); })
      .catch(() => {});
  }, [id]);

  // ── Enter edit mode ──────────────────────────────────────────────────────────
  const enterEditMode = async () => {
    // Lazily fetch lawyers list on first open
    if (lawyers.length === 0) {
      try {
        const res = await usersAPI.lawyers();
        setLawyers(res.data.results || res.data);
      } catch {
        toast.error('Could not load lawyers list');
      }
    }
    setEditForm({
      priority:           caseData.priority           || 'medium',
      assigned_lawyer:    caseData.assigned_lawyer     || null,
      supporting_lawyers: caseData.supporting_lawyers  || [],
      internal_notes:     caseData.internal_notes      || '',
    });
    setEditing(true);
  };

  const cancelEdit = () => setEditing(false);

  const saveEdit = async () => {
    setSaving(true);
    try {
      await casesAPI.update(id, {
        priority:           editForm.priority,
        assigned_lawyer:    editForm.assigned_lawyer || null,
        supporting_lawyers: editForm.supporting_lawyers,
        internal_notes:     editForm.internal_notes,
      });
      toast.success('Case updated!');
      setEditing(false);
      load();
    } catch {
      toast.error('Failed to save changes');
    } finally {
      if (mountedRef.current) setSaving(false);
    }
  };

  // ── Sync from eCourts ────────────────────────────────────────────────────────
  const syncCase = async () => {
    setSyncing(true);
    try {
      await casesAPI.syncCase(id);
      toast.success('Case synced from eCourts!');
      load();
    } catch (err) {
      if (err.response?.status === 502) {
        toast.error('Sync failed. Showing data from last sync.');
      } else {
        const msg = err.response?.data?.detail || err.response?.data?.error || 'Sync failed';
        toast.error(msg);
      }
    } finally {
      if (mountedRef.current) setSyncing(false);
    }
  };

  // ── AI Summary ───────────────────────────────────────────────────────────────
  const generateSummary = async () => {
    setSummaryLoading(true);
    setShowSummary(true);
    setSummary(null);
    try {
      const res = await casesAPI.generateSummary(id);
      setSummary(res.data);
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to generate summary';
      toast.error(msg);
      setShowSummary(false);
    }
    setSummaryLoading(false);
  };

  // ── Activity submit ──────────────────────────────────────────────────────────
  const submitActivity = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...actForm };
      if (!payload.new_hearing_date) delete payload.new_hearing_date;
      if (!payload.new_status) delete payload.new_status;
      await activitiesAPI.create(id, payload);
      toast.success('Activity added!');
      setShowActivityForm(false);
      setActForm({ activity_type: 'next_hearing', description: '', new_hearing_date: '', new_status: '' });
      load();
    } catch {
      toast.error('Failed to add activity');
    }
  };

  // ── Document upload ──────────────────────────────────────────────────────────
  const uploadDoc = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = '';
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('title', file.name);
      fd.append('doc_type', 'other');
      await documentsAPI.upload(id, fd);
      toast.success('Document uploaded!');
      load();
    } catch (err) {
      const msg = err.response?.data;
      toast.error(msg?.file?.[0] || msg?.detail || 'Upload failed');
    } finally {
      if (mountedRef.current) setUploading(false);
    }
  };

  // ── Send reminder ────────────────────────────────────────────────────────────
  const sendNotification = async () => {
    setSending(true);
    try {
      await notificationsAPI.sendManual(id);
      toast.success('Reminder sent to assigned lawyer!');
    } catch {
      toast.error('Failed to send notification');
    } finally {
      if (mountedRef.current) setSending(false);
    }
  };

  // ── Supporting-lawyers toggle ────────────────────────────────────────────────
  const toggleSupportingLawyer = (lawyerId) => {
    setEditForm(f => {
      const current = f.supporting_lawyers || [];
      return {
        ...f,
        supporting_lawyers: current.includes(lawyerId)
          ? current.filter(x => x !== lawyerId)
          : [...current, lawyerId],
      };
    });
  };

  // ── Loading / not found ──────────────────────────────────────────────────────
  if (loading) return <div className="text-center py-20 text-gray-400">Loading case details...</div>;
  if (!caseData) return <div className="text-center py-20 text-gray-400">Case not found</div>;

  // Nested arrays — safe access
  const hearingHistory  = caseData.hearing_history  || [];
  const interimOrders   = caseData.interim_orders   || [];
  const processes       = caseData.processes        || [];

  // Tab counts map
  const tabCount = {
    activities:      activities.length,
    hearing_history: hearingHistory.length,
    interim_orders:  interimOrders.length,
    processes:       processes.length,
    documents:       documents.length,
  };

  // ── RENDER ───────────────────────────────────────────────────────────────────
  return (
    <div className="max-w-5xl mx-auto space-y-5">

      {/* ── Back + Header ───────────────────────────────────────────────────── */}
      <div className="flex items-start gap-3">
        <button onClick={() => navigate('/cases')} className="mt-1 text-gray-400 hover:text-gray-700">
          <ArrowLeft size={20} />
        </button>

        {/* Title block */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-xl font-bold text-gray-900 font-mono tracking-wide">
              {caseData.cnr || caseData.case_number || '—'}
            </h1>
            <span className={`text-xs px-2 py-1 rounded-full font-medium ${statusColor(caseData.case_status)}`}>
              {caseData.case_status || 'Unknown'}
            </span>
          </div>
          <p className="text-gray-600 mt-0.5 text-sm truncate">
            {caseData.petitioners || '—'} vs. {caseData.respondents || '—'}
          </p>
          <p className="text-gray-400 text-xs mt-0.5">
            Last synced: {fmtDateTime(caseData.last_synced_at)}
          </p>
        </div>

        {/* Action buttons */}
        <div className="flex items-center gap-2 flex-wrap justify-end">
          {/* Sync */}
          <button
            onClick={syncCase}
            disabled={syncing}
            className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-2 rounded-xl text-sm font-medium transition-colors disabled:opacity-60"
          >
            <RefreshCw size={15} className={syncing ? 'animate-spin' : ''} />
            {syncing ? 'Syncing…' : 'Sync from eCourts'}
          </button>

          {/* Edit / Save / Cancel */}
          {!editing ? (
            <button
              onClick={enterEditMode}
              className="flex items-center gap-2 bg-gray-100 hover:bg-gray-200 text-gray-800 px-3 py-2 rounded-xl text-sm font-medium transition-colors"
            >
              <Edit2 size={15} /> Edit
            </button>
          ) : (
            <>
              <button
                onClick={saveEdit}
                disabled={saving}
                className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded-xl text-sm font-medium transition-colors disabled:opacity-60"
              >
                {saving ? 'Saving…' : 'Save'}
              </button>
              <button
                onClick={cancelEdit}
                className="flex items-center gap-2 bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-2 rounded-xl text-sm font-medium transition-colors"
              >
                Cancel
              </button>
            </>
          )}

          {/* AI Summary */}
          <button
            onClick={generateSummary}
            disabled={summaryLoading}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded-xl text-sm font-medium transition-colors disabled:opacity-60"
          >
            <Sparkles size={15} /> {summaryLoading ? 'Generating…' : 'AI Summary'}
          </button>

          {/* Send Reminder */}
          <button
            onClick={sendNotification}
            disabled={sending}
            className="flex items-center gap-2 bg-yellow-400 hover:bg-yellow-300 text-blue-950 px-3 py-2 rounded-xl text-sm font-medium transition-colors disabled:opacity-60"
          >
            <Bell size={15} /> {sending ? '…' : 'Send Reminder'}
          </button>
        </div>
      </div>

      {/* ── AI Summary Modal ─────────────────────────────────────────────────── */}
      {showSummary && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="ai-summary-modal-title"
            className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col"
          >
            <div className="flex items-center justify-between px-6 py-4 bg-gradient-to-r from-blue-600 to-blue-700 rounded-t-2xl">
              <div className="flex items-center gap-3">
                <div className="bg-white/20 p-1.5 rounded-lg">
                  <Sparkles size={18} className="text-white" />
                </div>
                <div>
                  <h2 id="ai-summary-modal-title" className="font-bold text-white text-base">AI Case Summary</h2>
                  {summary && (
                    <p className="text-blue-100 text-xs mt-0.5">
                      {summary.case_number} — {summary.title}
                    </p>
                  )}
                </div>
              </div>
              <button
                onClick={() => setShowSummary(false)}
                className="text-white/70 hover:text-white hover:bg-white/20 rounded-lg p-1.5 transition-colors"
              >
                <X size={18} />
              </button>
            </div>

            <div className="overflow-y-auto flex-1 px-6 py-5">
              {summaryLoading ? (
                <div className="flex flex-col items-center justify-center py-20 gap-4">
                  <div className="relative">
                    <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-100 border-t-blue-600" />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <Sparkles size={16} className="text-blue-600" />
                    </div>
                  </div>
                  <div className="text-center">
                    <p className="text-gray-700 font-medium text-sm">Generating AI Summary</p>
                    <p className="text-gray-400 text-xs mt-1">Analysing case details and documents…</p>
                  </div>
                </div>
              ) : summary ? (
                <div className="prose prose-sm max-w-none text-gray-800 leading-relaxed
                  [&>h1]:text-base [&>h1]:font-bold [&>h1]:text-gray-900 [&>h1]:mt-5 [&>h1]:mb-2 [&>h1]:border-b [&>h1]:pb-1
                  [&>h2]:text-sm [&>h2]:font-bold [&>h2]:text-gray-800 [&>h2]:mt-5 [&>h2]:mb-2
                  [&>h3]:text-sm [&>h3]:font-semibold [&>h3]:text-gray-700 [&>h3]:mt-4 [&>h3]:mb-1
                  [&>p]:text-sm [&>p]:text-gray-700 [&>p]:leading-relaxed [&>p]:mb-3
                  [&>ul]:text-sm [&>ul]:text-gray-700 [&>ul]:mb-3 [&>ul]:pl-5 [&>ul>li]:mb-1
                  [&>ol]:text-sm [&>ol]:text-gray-700 [&>ol]:mb-3 [&>ol]:pl-5 [&>ol>li]:mb-1
                  [&>strong]:font-semibold [&>strong]:text-gray-900
                  [&_strong]:font-semibold [&_strong]:text-gray-900
                  [&>hr]:border-gray-200 [&>hr]:my-4">
                  <ReactMarkdown>{summary.summary}</ReactMarkdown>
                </div>
              ) : null}
            </div>

            {summary && (
              <div className="px-6 py-3 border-t bg-gray-50 rounded-b-2xl flex justify-between items-center">
                <div className="flex items-center gap-1.5 text-xs text-gray-400">
                  <Sparkles size={11} />
                  <span>Generated at {new Date(summary.generated_at).toLocaleString()}</span>
                </div>
                <button
                  onClick={() => setShowSummary(false)}
                  className="text-sm px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
                >
                  Close
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Info Cards ───────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 items-start">

        {/* ── Case Details (3/4 width) ───────────────────────────────────────── */}
        <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 lg:col-span-3">
          <h2 className="font-semibold text-gray-700 text-sm border-b border-gray-100 pb-2 mb-4">
            Case Details
          </h2>

          {/* Edit-mode note */}
          {editing && (
            <div className="mb-4 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-700">
              Note: eCourts fields are read-only and updated via sync. Only internal fields can be edited here.
            </div>
          )}

          <div className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-4 text-sm">

            {/* Row 1: CNR | Case Number | Filing Number */}
            <div>
              <p className="text-xs text-gray-400 mb-0.5">CNR</p>
              <p className="font-semibold text-gray-800 font-mono">{caseData.cnr || '—'}</p>
            </div>
            <div>
              <p className="text-xs text-gray-400 mb-0.5">Case Number</p>
              <p className="font-semibold text-gray-800">{caseData.case_number || '—'}</p>
            </div>
            <div>
              <p className="text-xs text-gray-400 mb-0.5">Filing Number</p>
              <p className="font-semibold text-gray-800">{caseData.filing_number || '—'}</p>
            </div>

            {/* Row 2: Case Type | Case Status | Stage */}
            <div>
              <p className="text-xs text-gray-400 mb-0.5">Case Type</p>
              <p className="font-semibold text-gray-800">{caseData.case_type_raw || caseData.case_type || '—'}</p>
            </div>
            <div>
              <p className="text-xs text-gray-400 mb-0.5">Case Status</p>
              <span className={`inline-block text-xs px-2 py-0.5 rounded-full font-medium ${statusColor(caseData.case_status)}`}>
                {caseData.case_status || '—'}
              </span>
            </div>
            <div>
              <p className="text-xs text-gray-400 mb-0.5">Stage</p>
              <p className="font-semibold text-gray-800">{caseData.stage_of_case_raw || caseData.stage_of_case || '—'}</p>
            </div>

            {/* Row 3: Court Name | District | State */}
            <div>
              <p className="text-xs text-gray-400 mb-0.5">Court Name</p>
              <p className="font-semibold text-gray-800">{caseData.court_name || '—'}</p>
            </div>
            <div>
              <p className="text-xs text-gray-400 mb-0.5">District</p>
              <p className="font-semibold text-gray-800">{caseData.district || '—'}</p>
            </div>
            <div>
              <p className="text-xs text-gray-400 mb-0.5">State</p>
              <p className="font-semibold text-gray-800">{caseData.state || '—'}</p>
            </div>

            {/* Row 4: Filing Date | Registration Date | First Hearing Date */}
            <div>
              <p className="text-xs text-gray-400 mb-0.5">Filing Date</p>
              <p className="font-semibold text-gray-800">{fmtDate(caseData.filing_date)}</p>
            </div>
            <div>
              <p className="text-xs text-gray-400 mb-0.5">Registration Date</p>
              <p className="font-semibold text-gray-800">{fmtDate(caseData.registration_date)}</p>
            </div>
            <div>
              <p className="text-xs text-gray-400 mb-0.5">First Hearing Date</p>
              <p className="font-semibold text-gray-800">{fmtDate(caseData.first_hearing_date)}</p>
            </div>

            {/* Row 5: Last Hearing Date | Next Hearing Date | Hearing Count */}
            <div>
              <p className="text-xs text-gray-400 mb-0.5">Last Hearing Date</p>
              <p className="font-semibold text-gray-800">{fmtDate(caseData.last_hearing_date)}</p>
            </div>
            <div>
              <p className="text-xs text-gray-400 mb-0.5">Next Hearing Date</p>
              <p className="font-semibold text-gray-800">{fmtDate(caseData.next_hearing_date)}</p>
            </div>
            <div>
              <p className="text-xs text-gray-400 mb-0.5">Hearing Count</p>
              <p className="font-semibold text-gray-800">{caseData.hearing_count ?? '—'}</p>
            </div>

            {/* Row 6 — full-width text fields */}
            <div className="col-span-2 md:col-span-3">
              <p className="text-xs text-gray-400 mb-0.5">Judges</p>
              <p className="font-medium text-gray-700 leading-relaxed">{caseData.judges || '—'}</p>
            </div>
            <div className="col-span-2 md:col-span-3">
              <p className="text-xs text-gray-400 mb-0.5">Petitioners</p>
              <p className="font-medium text-gray-700 leading-relaxed">{caseData.petitioners || '—'}</p>
            </div>
            <div className="col-span-2 md:col-span-3">
              <p className="text-xs text-gray-400 mb-0.5">Respondents</p>
              <p className="font-medium text-gray-700 leading-relaxed">{caseData.respondents || '—'}</p>
            </div>
            <div className="col-span-2 md:col-span-3">
              <p className="text-xs text-gray-400 mb-0.5">Petitioner Advocates</p>
              <p className="font-medium text-gray-700 leading-relaxed">{caseData.petitioner_advocates || '—'}</p>
            </div>
            <div className="col-span-2 md:col-span-3">
              <p className="text-xs text-gray-400 mb-0.5">Respondent Advocates</p>
              <p className="font-medium text-gray-700 leading-relaxed">{caseData.respondent_advocates || '—'}</p>
            </div>
            <div className="col-span-2 md:col-span-3">
              <p className="text-xs text-gray-400 mb-0.5">Purpose</p>
              <p className="font-medium text-gray-700 leading-relaxed">{caseData.purpose || '—'}</p>
            </div>

            {/* Internal Notes — editable in edit mode */}
            <div className="col-span-2 md:col-span-3">
              <p className="text-xs text-gray-400 mb-0.5">Internal Notes</p>
              {editing ? (
                <textarea
                  className={inputCls}
                  rows={3}
                  value={editForm.internal_notes}
                  onChange={e => setEditForm(f => ({ ...f, internal_notes: e.target.value }))}
                  placeholder="Internal notes…"
                />
              ) : (
                <p className="font-medium text-gray-700 leading-relaxed">{caseData.internal_notes || '—'}</p>
              )}
            </div>

            {/* Row 7: Assigned Lawyer | Priority | Supporting Lawyers */}
            <div>
              <p className="text-xs text-gray-400 mb-0.5">Assigned Lawyer</p>
              {editing ? (
                <select
                  className={inputCls}
                  value={editForm.assigned_lawyer ?? ''}
                  onChange={e => setEditForm(f => ({ ...f, assigned_lawyer: e.target.value ? Number(e.target.value) : null }))}
                >
                  <option value="">Unassigned</option>
                  {lawyers.map(l => (
                    <option key={l.id} value={l.id}>
                      {l.full_name || `${l.first_name} ${l.last_name}`.trim() || l.username}
                    </option>
                  ))}
                </select>
              ) : (
                <p className="font-semibold text-gray-800">
                  {caseData.assigned_lawyer_detail?.full_name || '—'}
                </p>
              )}
            </div>

            <div>
              <p className="text-xs text-gray-400 mb-0.5">Priority</p>
              {editing ? (
                <select
                  className={inputCls}
                  value={editForm.priority}
                  onChange={e => setEditForm(f => ({ ...f, priority: e.target.value }))}
                >
                  <option value="high">High</option>
                  <option value="medium">Medium</option>
                  <option value="low">Low</option>
                </select>
              ) : (
                <p className={`font-semibold capitalize ${priorityColor(caseData.priority)}`}>
                  {caseData.priority || '—'}
                </p>
              )}
            </div>

            <div>
              <p className="text-xs text-gray-400 mb-0.5">Supporting Lawyers</p>
              {editing ? (
                <div className="space-y-1 max-h-36 overflow-y-auto border border-gray-200 rounded-xl p-2">
                  {lawyers.length === 0 && (
                    <p className="text-xs text-gray-400">No lawyers available</p>
                  )}
                  {lawyers.map(l => (
                    <label key={l.id} className="flex items-center gap-2 text-sm cursor-pointer">
                      <input
                        type="checkbox"
                        checked={(editForm.supporting_lawyers || []).includes(l.id)}
                        onChange={() => toggleSupportingLawyer(l.id)}
                        className="rounded"
                      />
                      {l.full_name || `${l.first_name} ${l.last_name}`.trim() || l.username}
                    </label>
                  ))}
                </div>
              ) : (
                <p className="font-semibold text-gray-800">
                  {(caseData.supporting_lawyers_detail || []).map(l =>
                    l.full_name || `${l.first_name} ${l.last_name}`.trim() || l.username
                  ).join(', ') || '—'}
                </p>
              )}
            </div>

          </div>
        </div>

        {/* ── Next Hearing widget (1/4 width) ───────────────────────────────── */}
        <div className="rounded-xl overflow-hidden shadow-sm" style={{ backgroundColor: '#1a2744' }}>
          <div className="px-4 py-2.5 border-b border-white/10">
            <p className="text-xs font-semibold text-blue-300 uppercase tracking-wider">Next Hearing</p>
          </div>
          {caseData.next_hearing_date ? (
            <div className="px-4 py-5 text-center">
              <p className="text-5xl font-bold text-white leading-none">
                {format(new Date(caseData.next_hearing_date), 'dd')}
              </p>
              <p className="text-blue-300 text-sm font-medium mt-1">
                {format(new Date(caseData.next_hearing_date), 'MMM yyyy')}
              </p>
              {caseData.purpose && (
                <p className="text-blue-400 text-xs mt-3 leading-relaxed">
                  Purpose: {caseData.purpose}
                </p>
              )}
            </div>
          ) : (
            <div className="px-4 py-5 text-center">
              <p className="text-blue-400 text-xs leading-relaxed">No hearing<br />scheduled yet</p>
            </div>
          )}
        </div>

      </div>

      {/* ── Tabs ─────────────────────────────────────────────────────────────── */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">

        {/* Tab bar */}
        <div className="flex border-b border-gray-100 overflow-x-auto">
          {TABS.map(({ key, label, Icon }) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              className={`flex items-center gap-2 px-4 py-3.5 text-sm font-medium whitespace-nowrap transition-colors ${
                tab === key
                  ? 'border-b-2 border-blue-950 text-blue-950'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <Icon size={15} />
              {label}
              <span className="text-xs bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded-full">
                {tabCount[key]}
              </span>
            </button>
          ))}

          {/* Spacer + context actions */}
          <div className="flex-1" />
          {tab === 'activities' && (
            <button
              onClick={() => setShowActivityForm(!showActivityForm)}
              className="flex items-center gap-1 px-4 py-3 text-sm font-medium text-blue-900 hover:text-blue-700 whitespace-nowrap"
            >
              <Plus size={15} /> Add Activity
            </button>
          )}
          {tab === 'documents' && (
            <label className="flex items-center gap-1 px-4 py-3 text-sm font-medium text-blue-900 hover:text-blue-700 cursor-pointer whitespace-nowrap">
              <Upload size={15} /> {uploading ? 'Uploading…' : 'Upload'}
              <input type="file" className="hidden" onChange={uploadDoc} />
            </label>
          )}
        </div>

        {/* ── Activity Form ────────────────────────────────────────────────── */}
        {tab === 'activities' && showActivityForm && (
          <form onSubmit={submitActivity} className="p-5 bg-blue-50 border-b border-gray-100 space-y-3">
            <h3 className="font-semibold text-gray-700 text-sm">Add Activity</h3>
            <div className="flex gap-3 items-end">
              <div className="flex-1">
                <select
                  className={inputCls}
                  value={actForm.activity_type}
                  onChange={e => setActForm(f => ({ ...f, activity_type: e.target.value, new_hearing_date: '', new_status: '' }))}
                >
                  <option value="status_update">Status Update</option>
                  <option value="next_hearing">Next Hearing</option>
                  <option value="other">Other</option>
                </select>
              </div>

              {actForm.activity_type === 'next_hearing' && (
                <div className="w-44">
                  <label className="block text-xs text-gray-500 mb-1">Next Hearing Date *</label>
                  <input
                    type="date"
                    className={inputCls}
                    value={actForm.new_hearing_date}
                    onChange={e => setActForm(f => ({ ...f, new_hearing_date: e.target.value }))}
                    required
                  />
                </div>
              )}

              {actForm.activity_type === 'status_update' && (
                <div className="w-56">
                  <label className="block text-xs text-gray-500 mb-1">New Case Stage *</label>
                  <select
                    className={inputCls}
                    value={actForm.new_status}
                    onChange={e => setActForm(f => ({ ...f, new_status: e.target.value }))}
                    required
                  >
                    <option value="">-- Select Stage --</option>
                    {caseStages.map(s => (
                      <option key={s.id} value={s.name}>{s.name}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            <textarea
              className={inputCls}
              rows={3}
              placeholder="Lawyer Remark *"
              value={actForm.description}
              onChange={e => setActForm(f => ({ ...f, description: e.target.value }))}
              required
            />

            <div className="flex gap-2">
              <button type="submit" className="bg-blue-950 text-white px-4 py-2 rounded-xl text-sm font-medium hover:bg-blue-800">
                Save Activity
              </button>
              <button
                type="button"
                onClick={() => setShowActivityForm(false)}
                className="px-4 py-2 border rounded-xl text-sm text-gray-600 hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </form>
        )}

        {/* ── Tab Content ──────────────────────────────────────────────────── */}

        {/* Activities */}
        {tab === 'activities' && (
          <div className="divide-y divide-gray-50">
            {activities.length === 0 ? (
              <p className="text-center text-gray-400 py-10 text-sm">No activities logged yet</p>
            ) : activities.map(a => (
              <div key={a.id} className="px-5 py-4">
                <div className="flex items-start gap-3">
                  <div className="mt-1 w-2 h-2 rounded-full bg-blue-950 shrink-0" />
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full capitalize font-medium">
                        {a.title}
                      </span>
                      {a.new_status && (
                        <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                          → {a.new_status}
                        </span>
                      )}
                      {a.new_hearing_date && (
                        <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded-full">
                          Next: {fmtDate(a.new_hearing_date)}
                        </span>
                      )}
                    </div>
                    {a.description && <p className="text-sm text-gray-600 mt-1">{a.description}</p>}
                    <p className="text-xs text-gray-400 mt-1">
                      {fmtDate(a.created_at)} · {a.created_by_detail?.full_name}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Hearing History */}
        {tab === 'hearing_history' && (
          hearingHistory.length === 0 ? (
            <p className="text-center text-gray-400 py-10 text-sm">No hearing history available</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 text-left">
                    <th className="px-5 py-3 text-xs text-gray-500 font-semibold">Judge</th>
                    <th className="px-5 py-3 text-xs text-gray-500 font-semibold">Business Date</th>
                    <th className="px-5 py-3 text-xs text-gray-500 font-semibold">Hearing Date</th>
                    <th className="px-5 py-3 text-xs text-gray-500 font-semibold">Purpose of Listing</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {hearingHistory.map(h => (
                    <tr key={h.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-5 py-3.5 text-gray-700">{h.judge || '—'}</td>
                      <td className="px-5 py-3.5 text-gray-700">{fmtDate(h.business_on_date)}</td>
                      <td className="px-5 py-3.5 text-gray-700">{fmtDate(h.hearing_date)}</td>
                      <td className="px-5 py-3.5 text-gray-700">{h.purpose_of_listing || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        )}

        {/* Interim Orders */}
        {tab === 'interim_orders' && (
          interimOrders.length === 0 ? (
            <p className="text-center text-gray-400 py-10 text-sm">No interim orders available</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 text-left">
                    <th className="px-5 py-3 text-xs text-gray-500 font-semibold">Order Date</th>
                    <th className="px-5 py-3 text-xs text-gray-500 font-semibold">Description</th>
                    <th className="px-5 py-3 text-xs text-gray-500 font-semibold">Order</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {interimOrders.map(o => (
                    <tr key={o.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-5 py-3.5 text-gray-700 whitespace-nowrap">{fmtDate(o.order_date)}</td>
                      <td className="px-5 py-3.5 text-gray-700">{o.description || '—'}</td>
                      <td className="px-5 py-3.5">
                        {o.order_url ? (
                          <a
                            href={o.order_url}
                            target="_blank"
                            rel="noreferrer"
                            className="text-blue-700 hover:underline font-medium text-xs px-3 py-1.5 border border-blue-200 rounded-lg"
                          >
                            View
                          </a>
                        ) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        )}

        {/* Processes */}
        {tab === 'processes' && (
          processes.length === 0 ? (
            <p className="text-center text-gray-400 py-10 text-sm">No processes recorded</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 text-left">
                    <th className="px-5 py-3 text-xs text-gray-500 font-semibold">Process ID</th>
                    <th className="px-5 py-3 text-xs text-gray-500 font-semibold">Date</th>
                    <th className="px-5 py-3 text-xs text-gray-500 font-semibold">Title</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {processes.map(p => (
                    <tr key={p.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-5 py-3.5 text-gray-700 font-mono">{p.process_id || '—'}</td>
                      <td className="px-5 py-3.5 text-gray-700 whitespace-nowrap">{fmtDate(p.process_date)}</td>
                      <td className="px-5 py-3.5 text-gray-700">{p.process_title || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        )}

        {/* Documents */}
        {tab === 'documents' && (
          <div className="divide-y divide-gray-50">
            {documents.length === 0 ? (
              <p className="text-center text-gray-400 py-10 text-sm">No documents uploaded yet</p>
            ) : documents.map(d => (
              <div key={d.id} className="flex items-center gap-4 px-5 py-3.5">
                <div className="p-2 bg-blue-50 rounded-lg">
                  <FileText size={18} className="text-blue-900" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-800">{d.title}</p>
                  <p className="text-xs text-gray-400">
                    {d.doc_type}{d.file_size != null ? ` · ${Math.round(d.file_size / 1024)} KB` : ''} · {fmtDate(d.uploaded_at)}
                  </p>
                </div>
                <a
                  href={d.file_url}
                  target="_blank"
                  rel="noreferrer"
                  className="text-xs text-blue-700 hover:underline font-medium px-3 py-1.5 border border-blue-200 rounded-lg"
                >
                  Download
                </a>
              </div>
            ))}
          </div>
        )}

      </div>
    </div>
  );
}
